import { useEffect } from "react";

/**
 * SEO / AEO / GEO head manager.
 *
 * - Updates <title>, meta description, robots, canonical, OG, Twitter.
 * - Injects JSON-LD blocks per page: Attorney (LegalService), Person,
 *   BreadcrumbList, FAQPage, plus any page-specific schema passed via `schema`.
 * - Speakable selectors are attached to the Attorney schema so voice
 *   assistants (Alexa, Google Assistant) and answer engines can read the
 *   firm summary directly.
 */
const SITE_URL = "https://joehelfrickinjuryattorney.com";

function upsertMeta({ name, property, content }) {
    if (content === undefined || content === null) return;
    const selector = name
        ? `meta[name="${name}"]`
        : `meta[property="${property}"]`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        if (name) el.setAttribute("name", name);
        if (property) el.setAttribute("property", property);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertLink(rel, href, extra = {}) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
}

function upsertJsonLd(id, data) {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

const PRACTICE_AREA_OFFERS = [
    {
        name: "Car & Truck Accident Representation",
        url: "/practice-areas#car-and-truck-accidents",
    },
    { name: "Slip, Trip & Fall", url: "/practice-areas#slip-and-fall" },
    {
        name: "Medical & Dental Malpractice",
        url: "/practice-areas#medical-and-dental-malpractice",
    },
    {
        name: "Dog Bites & Animal Attacks",
        url: "/practice-areas#dog-bites-and-animal-attacks",
    },
    {
        name: "Workplace Accidents",
        url: "/practice-areas#workplace-accidents",
    },
    {
        name: "Ski Accidents, Government Claims, Property Disputes",
        url: "/practice-areas#other-claims",
    },
];

export default function SEO({
    title,
    description,
    path = "/",
    image,
    schema,
    faqs,
    breadcrumbs,
}) {
    useEffect(() => {
        const fullTitle = title
            ? `${title} | Joseph G. Helfrick — Sacramento Personal Injury Attorney`
            : "Joseph G. Helfrick — Sacramento Personal Injury Attorney";
        document.title = fullTitle;

        const ogImage = image || `${SITE_URL}/images/og-cover.jpg`;

        upsertMeta({ name: "description", content: description });
        upsertMeta({
            name: "keywords",
            content:
                "Sacramento personal injury attorney, Sacramento personal injury lawyer, Placer County injury attorney, car accident lawyer Sacramento, truck accident attorney Sacramento, semi-truck accident lawyer, medical malpractice attorney Sacramento, dental malpractice lawyer, dog bite attorney California, slip and fall lawyer Sacramento, slip and fall lawyer Roseville, workplace injury attorney Sacramento, ski accident attorney Tahoe, government tort claim California, Roseville injury lawyer, Rocklin injury attorney, Folsom personal injury, Elk Grove accident attorney, Auburn injury lawyer, Joseph Helfrick, Joe Helfrick, Helfrick attorney",
        });
        upsertMeta({
            name: "robots",
            content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
        });
        upsertMeta({ name: "googlebot", content: "index,follow" });
        upsertMeta({ name: "author", content: "Joseph G. Helfrick" });
        upsertMeta({ name: "geo.region", content: "US-CA" });
        upsertMeta({
            name: "geo.placename",
            content: "Sacramento, California",
        });
        upsertMeta({ name: "geo.position", content: "38.5816;-121.4944" });
        upsertMeta({ name: "ICBM", content: "38.5816, -121.4944" });

        // Open Graph
        upsertMeta({ property: "og:type", content: "website" });
        upsertMeta({ property: "og:locale", content: "en_US" });
        upsertMeta({ property: "og:title", content: fullTitle });
        upsertMeta({ property: "og:description", content: description });
        upsertMeta({ property: "og:url", content: `${SITE_URL}${path}` });
        upsertMeta({ property: "og:image", content: ogImage });
        upsertMeta({ property: "og:image:width", content: "1200" });
        upsertMeta({ property: "og:image:height", content: "630" });
        upsertMeta({
            property: "og:site_name",
            content: "Joseph G. Helfrick, Attorney at Law",
        });
        // Twitter
        upsertMeta({ name: "twitter:card", content: "summary_large_image" });
        upsertMeta({ name: "twitter:title", content: fullTitle });
        upsertMeta({ name: "twitter:description", content: description });
        upsertMeta({ name: "twitter:image", content: ogImage });

        upsertLink("canonical", `${SITE_URL}${path}`);
        upsertLink("alternate", `${SITE_URL}${path}`, { hreflang: "en-us" });

        // ---- LegalService / Attorney ---------------------------------
        const legalService = {
            "@context": "https://schema.org",
            "@type": ["LegalService", "Attorney"],
            "@id": `${SITE_URL}/#attorney`,
            name: "Joseph G. Helfrick — Personal Injury Attorney",
            alternateName: ["Joe Helfrick", "Helfrick Law"],
            url: SITE_URL,
            image: `${SITE_URL}/images/joe-helfrick-portrait.jpg`,
            logo: `${SITE_URL}/images/og-cover.jpg`,
            telephone: "+1-916-492-2000",
            email: "JHelfrick@ltglaw.net",
            priceRange: "Free consultation — contingency fee (no fee unless we win)",
            description:
                "Joseph G. Helfrick is a Sacramento-born personal injury attorney representing the seriously injured throughout Sacramento, CA, and surrounding areas. Cases include car and truck accidents, slip and fall, medical and dental malpractice, dog bites, workplace injuries, ski accidents, and government tort claims.",
            slogan: "No fee unless we win.",
            address: {
                "@type": "PostalAddress",
                streetAddress: "1755 Creekside Oaks Drive, Suite 240",
                addressLocality: "Sacramento",
                addressRegion: "CA",
                postalCode: "95833",
                addressCountry: "US",
            },
            geo: {
                "@type": "GeoCoordinates",
                latitude: 38.5816,
                longitude: -121.4944,
            },
            hasMap:
                "https://www.google.com/maps/search/?api=1&query=1755+Creekside+Oaks+Drive+Suite+240+Sacramento+CA+95833",
            openingHoursSpecification: [
                {
                    "@type": "OpeningHoursSpecification",
                    dayOfWeek: [
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                    ],
                    opens: "08:30",
                    closes: "17:30",
                },
            ],
            areaServed: [
                {
                    "@type": "AdministrativeArea",
                    name: "Sacramento County, California",
                },
                {
                    "@type": "AdministrativeArea",
                    name: "Placer County, California",
                },
                {
                    "@type": "City",
                    name: "Sacramento",
                    containedInPlace: {
                        "@type": "State",
                        name: "California",
                    },
                },
                { "@type": "City", name: "Roseville" },
                { "@type": "City", name: "Rocklin" },
                { "@type": "City", name: "Elk Grove" },
                { "@type": "City", name: "Folsom" },
                { "@type": "City", name: "Auburn" },
                { "@type": "City", name: "Citrus Heights" },
                { "@type": "City", name: "Rancho Cordova" },
            ],
            serviceType: [
                "Personal Injury Law",
                "Car Accident Litigation",
                "Truck Accident Litigation",
                "Medical Malpractice Litigation",
                "Dental Malpractice Litigation",
                "Premises Liability (Slip and Fall)",
                "Dog Bite & Animal Attack Claims",
                "Workplace Injury (Third-Party) Claims",
                "Ski & Recreational Injury Claims",
                "Government Tort Claims",
                "Wrongful Death",
            ],
            hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Personal Injury Practice Areas",
                itemListElement: PRACTICE_AREA_OFFERS.map((p, i) => ({
                    "@type": "Offer",
                    position: i + 1,
                    itemOffered: {
                        "@type": "Service",
                        name: p.name,
                        url: `${SITE_URL}${p.url}`,
                    },
                })),
            },
            knowsLanguage: ["English"],
            knowsAbout: [
                "California Personal Injury Law",
                "California Civil Code § 3294 (Punitive Damages)",
                "California Civil Code § 3342 (Dog Bite Strict Liability)",
                "California Government Code § 911.2 (Tort Claims)",
                "MICRA — Medical Injury Compensation Reform Act",
                "Comparative Negligence",
                "Uninsured / Underinsured Motorist Claims",
            ],
            founder: { "@id": `${SITE_URL}/#person` },
            employee: { "@id": `${SITE_URL}/#person` },
            sameAs: ["https://ltglaw.net/team/joseph-helfrick/"],
            speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: [
                    "[data-speakable]",
                    "h1",
                    ".tldr",
                ],
            },
        };
        upsertJsonLd("ld-attorney", legalService);

        // ---- Person (E-E-A-T) ----------------------------------------
        const person = {
            "@context": "https://schema.org",
            "@type": "Person",
            "@id": `${SITE_URL}/#person`,
            name: "Joseph G. Helfrick",
            givenName: "Joseph",
            additionalName: "G.",
            familyName: "Helfrick",
            alternateName: "Joe Helfrick",
            image: `${SITE_URL}/images/joe-helfrick-portrait.jpg`,
            jobTitle: "Personal Injury Attorney",
            description:
                "Sacramento-born trial attorney. Holy Cross College of Notre Dame (B.A. History, 2010); Lincoln Law School (J.D., 2015 — Faculty Achievement Award, Legal Analysis); admitted to the California State Bar (2017) and the U.S. District Court for the Eastern District of California (2020).",
            url: `${SITE_URL}/about`,
            worksFor: { "@id": `${SITE_URL}/#attorney` },
            alumniOf: [
                {
                    "@type": "CollegeOrUniversity",
                    name: "Holy Cross College of Notre Dame",
                },
                { "@type": "EducationalOrganization", name: "Lincoln Law School" },
                {
                    "@type": "HighSchool",
                    name: "Jesuit High School (Sacramento)",
                },
            ],
            hasCredential: [
                {
                    "@type": "EducationalOccupationalCredential",
                    name: "California State Bar Admission",
                    credentialCategory: "license",
                    recognizedBy: { "@type": "Organization", name: "State Bar of California" },
                    dateCreated: "2017",
                },
                {
                    "@type": "EducationalOccupationalCredential",
                    name: "U.S. District Court, Eastern District of California Admission",
                    credentialCategory: "license",
                    dateCreated: "2020",
                },
                {
                    "@type": "EducationalOccupationalCredential",
                    name: "Juris Doctor",
                    credentialCategory: "degree",
                    educationalLevel: "Doctoral",
                    recognizedBy: { "@type": "Organization", name: "Lincoln Law School" },
                    dateCreated: "2015",
                },
            ],
            memberOf: [
                {
                    "@type": "Organization",
                    name: "Association of Defense Counsel of Northern California and Nevada",
                },
                { "@type": "Organization", name: "Sacramento County Bar Association" },
            ],
            knowsAbout: [
                "Personal Injury Law",
                "Medical Malpractice Litigation",
                "Insurance Defense and Plaintiff Litigation",
            ],
            sameAs: ["https://ltglaw.net/team/joseph-helfrick/"],
        };
        upsertJsonLd("ld-person", person);

        // ---- Breadcrumbs ---------------------------------------------
        if (breadcrumbs && breadcrumbs.length) {
            upsertJsonLd("ld-breadcrumbs", {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbs.map((b, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: b.name,
                    item: `${SITE_URL}${b.path}`,
                })),
            });
        } else {
            const el = document.getElementById("ld-breadcrumbs");
            if (el) el.remove();
        }

        // ---- FAQ ------------------------------------------------------
        if (faqs && faqs.length) {
            upsertJsonLd("ld-faq", {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
            });
        } else {
            const el = document.getElementById("ld-faq");
            if (el) el.remove();
        }

        // ---- Page-specific schema ------------------------------------
        if (schema) {
            upsertJsonLd("ld-page", schema);
        } else {
            const el = document.getElementById("ld-page");
            if (el) el.remove();
        }
    }, [title, description, path, image, schema, faqs, breadcrumbs]);

    return null;
}
